import {
  faCheck,
  faDownload,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ENV } from "../../util/Env";
import NotificationModal from "../events/NotificationModal";
import DspFooter from "../events/DspFooter";
import DspHeader from "../events/DspHeader";

const ZipDownload = () => {
  const { fileId, email } = useParams();
  const [loading, setLoading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState("");
  const [downloadComplete, setDownloadComplete] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [fileName, setFileName] = useState("");
  const hasDownloadStarted = useRef(false);
  const navigate = useNavigate();

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("success");
  const [modalMessage, setModalMessage] = useState("");
  const [modalTitle, setModalTitle] = useState("");

  useEffect(() => {
    if (fileId && email && !hasDownloadStarted.current) {
      hasDownloadStarted.current = true;
      setDownloadStatus("Initiating download...");
      downloadZip();
    }
  }, [fileId, email]);

  const extractFileNameFromHeader = (contentDisposition) => {
    const defaultFileName = `Event_Details_${new Date().toISOString().slice(0, 10)}.zip`;
    
    if (!contentDisposition) {
      return defaultFileName;
    }

    const fileNameMatch = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
    );
    if (fileNameMatch && fileNameMatch[1]) {
      return fileNameMatch[1].replace(/['"]/g, "");
    }

    const fileNameStarMatch = contentDisposition.match(
      /filename\*=(?:UTF-8'')?(.+)/,
    );
    if (fileNameStarMatch && fileNameStarMatch[1]) {
      return decodeURIComponent(fileNameStarMatch[1]);
    }

    return defaultFileName;
  };

  const updateProgressStatus = (progress, totalBytes) => {
    if (totalBytes) {
      const calculatedProgress = Math.round((progress / totalBytes) * 90);
      setDownloadProgress(Math.min(calculatedProgress + 5, 90));

      if (calculatedProgress < 30) {
        setDownloadStatus("Downloading...");
      } else if (calculatedProgress < 60) {
        setDownloadStatus("Processing...");
      } else if (calculatedProgress < 90) {
        setDownloadStatus("Finalizing...");
      }
    } else {
      const timeBasedProgress = Math.min((progress / 1000000) * 50, 80);
      setDownloadProgress(Math.min(timeBasedProgress + 5, 85));
      setDownloadStatus("Downloading...");
    }
  };

  const handleDownloadError = (error) => {
    console.error("Download error:", error);
    setDownloadProgress(0);
    setDownloadError(true);

    const errorInfo = getErrorInfo(error);
    setDownloadStatus(errorInfo.status);
    setModalType("error");
    setModalTitle("Download Failed!");
    setModalMessage(errorInfo.message);
    setShowModal(true);
  };

  const getErrorInfo = (error) => {
    const errorMessage = error.message || "";

    if (errorMessage.includes("HTTP error")) {
      if (errorMessage.includes("404")) {
        return { message: "File not found on server", status: "File not found" };
      }
      if (errorMessage.includes("403")) {
        return { message: "Access denied to download file", status: "Access denied" };
      }
      if (errorMessage.includes("500")) {
        return { message: "Server error occurred", status: "Server error" };
      }
      return { message: `Server responded with error: ${errorMessage}`, status: "Download failed" };
    }

    if (errorMessage.includes("Failed to fetch")) {
      return { message: "Network error - please check your connection", status: "Network error" };
    }

    return { message: errorMessage || "An unexpected error occurred", status: "Download failed" };
  };

  const combineChunks = (chunks) => {
    const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const combinedArray = new Uint8Array(totalLength);
    let offset = 0;

    for (const chunk of chunks) {
      combinedArray.set(chunk, offset);
      offset += chunk.length;
    }

    return combinedArray;
  };

  const triggerFileDownload = (blob, fileName) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const showSuccessModal = () => {
    setTimeout(() => {
      setModalType("success");
      setModalTitle("Success!");
      setModalMessage("File downloaded successfully");
      setShowModal(true);
    }, 1000);
  };

  const downloadZip = async () => {
    const url = ENV.API_URL + `files/files/temp/download`;

    try {
      setDownloadStatus("Connecting...");
      setDownloadProgress(0);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: ``,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: fileId,
          email: email,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      setDownloadStatus("Downloading...");
      setDownloadProgress(5);

      const contentLength = response.headers.get("Content-Length");
      const totalBytes = contentLength ? parseInt(contentLength, 10) : null;
      const contentDisposition = response.headers.get("Content-Disposition");
      const downloadFileName = extractFileNameFromHeader(contentDisposition);

      setFileName(downloadFileName);
      const reader = response.body.getReader();
      const chunks = [];
      let receivedBytes = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
        receivedBytes += value.length;
        updateProgressStatus(receivedBytes, totalBytes);
      }

      setDownloadStatus("Creating file...");
      setDownloadProgress(95);
      const combinedArray = combineChunks(chunks);
      const blob = new Blob([combinedArray], { type: "application/zip" });

      setDownloadStatus("Preparing download...");
      setDownloadProgress(98);
      triggerFileDownload(blob, downloadFileName);

      setDownloadStatus("Completed");
      setDownloadProgress(100);
      setDownloadComplete(true);
      showSuccessModal();
    } catch (error) {
      handleDownloadError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <React.Fragment>
      <DspHeader />
      <div className="download_wrapper">
        <div className="download_container">
          <div className="download_card">
            <div className="progress_circle">
              <svg
                className="progress_ring"
                viewBox="0 0 200 200"
                width="200"
                height="200"
              >
                <circle
                  className="progress_ring_bg"
                  stroke="#e6e6e6"
                  strokeWidth="8"
                  fill="transparent"
                  r="90"
                  cx="100"
                  cy="100"
                />
                <circle
                  className="progress_ring_fill"
                  stroke={
                    downloadError
                      ? "#ff4757"
                      : downloadComplete
                        ? "#2ed573"
                        : "rgba(var(--btn-bg-color))"
                  }
                  strokeWidth="8"
                  fill="transparent"
                  r="90"
                  cx="100"
                  cy="100"
                  strokeDasharray={`${2 * Math.PI * 90}`}
                  strokeDashoffset={`${2 * Math.PI * 90 * (1 - downloadProgress / 100)}`}
                  style={{
                    transition: "stroke-dashoffset 0.5s ease-in-out",
                    transform: "rotate(-90deg)",
                    transformOrigin: "50% 50%",
                  }}
                />
              </svg>

              <div className="center_content">
                <div className="download_icon">
                  {downloadError ? (
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      className="icon error"
                    />
                  ) : downloadComplete ? (
                    <FontAwesomeIcon icon={faCheck} className="icon success" />
                  ) : (
                    <FontAwesomeIcon
                      icon={faDownload}
                      className={`icon downloading ${loading ? "animate" : ""}`}
                    />
                  )}
                </div>

                {!downloadError && (
                  <div className="percentage_text">
                    {downloadComplete ? "Done" : `${downloadProgress}%`}
                  </div>
                )}

                {downloadError && <div className="error_text">Failed</div>}
              </div>
            </div>

            <div className="status_container">
              <h2 className="download_title">File Download</h2>
              {/* {fileName && (
                <p className="file_name">
                  <strong>File:</strong> {fileName}
                </p>
              )} */}
              <p className="status_message">
                {downloadStatus || "Preparing..."}
              </p>
              {!downloadError && !downloadComplete && (
                <div className="loading_dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Footer */}
      </div>
      <DspFooter />

      {/* Notification Modal */}
      <NotificationModal
        isOpen={showModal}
        type={modalType}
        message={modalMessage}
        title={modalTitle}
        onClose={() => {
          setShowModal(false);
          setModalMessage("");
          setModalTitle("");
        }}
      />
    </React.Fragment>
  );
};

export default ZipDownload;
